import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useTranslation } from 'react-i18next';

export default function AdminAuditLogsPage() {
  const apiClient = useApiClient();
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(0);
  const [actionFilter, setActionFilter] = useState<string>('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [establishmentFilter, setEstablishmentFilter] = useState<string>('');
  const pageSize = 50;

  // Fetch users for filter dropdown
  const usersQuery = useQuery({
    queryKey: ['admin:users'],
    queryFn: () => apiClient.getAdminUsers(),
  });

  // Fetch establishments for filter dropdown
  const establishmentsQuery = useQuery({
    queryKey: ['admin:establishments-filter'],
    queryFn: () => apiClient.getAdminEstablishments(0, 500),
  });

  const auditLogsQuery = useQuery({
    queryKey: ['admin:audit-logs', page, actionFilter, userFilter, establishmentFilter],
    queryFn: () =>
      apiClient.getAuditLogs(page * pageSize, pageSize, {
        action: actionFilter || undefined,
        userId: userFilter || undefined,
        establishmentId: establishmentFilter || undefined,
      }),
  });

  const logs = auditLogsQuery.data?.data || [];
  const total = auditLogsQuery.data?.total || 0;
  const users = usersQuery.data || [];
  const establishments = establishmentsQuery.data?.data || [];

  // Get unique actions for filtering from currently loaded logs
  const uniqueActions: string[] = Array.from(new Set(logs.map((log: any) => String(log.action))));

  // User-friendly action names
  const getActionLabel = (action: string) => {
    const key = `admin.auditLog.actions.${action}`;
    const translated = t(key);
    return translated === key ? action : translated;
  };

  // Get action badge color
  const getActionColor = (action: string) => {
    if (action.includes('CREATE') || action.includes('COMPLETED') || action.includes('UPLOADED')) return 'bg-green-100 text-green-800';
    if (action.includes('UPDATE') || action.includes('UPSERTED') || action.includes('CHANGED')) return 'bg-blue-100 text-blue-800';
    if (action.includes('DELETE') || action.includes('DEACTIVATED') || action.includes('CANCELED')) return 'bg-red-100 text-red-800';
    if (action.includes('LOGIN') || action.includes('LOGOUT')) return 'bg-purple-100 text-purple-800';
    if (action.includes('CLOSED')) return 'bg-amber-100 text-amber-800';
    if (action.includes('INCIDENT') || action.includes('TEMPERATURE')) return 'bg-orange-100 text-orange-800';
    return 'bg-neutral-100 text-neutral-800';
  };

  // User-friendly entity type names
  const getEntityLabel = (entityType: string) => {
    const key = `admin.auditLog.entities.${entityType}`;
    const translated = t(key);
    return translated === key ? entityType : translated;
  };

  // Get role display name
  const getRoleDisplay = (role?: string) => {
    if (!role) return '-';
    return t(`roles.${role}`, { defaultValue: role });
  };

  // Get role badge color
  const getRoleColor = (role?: string) => {
    const colors: Record<string, string> = {
      OWNER: 'bg-blue-100 text-blue-800',
      STAFF: 'bg-green-100 text-green-800',
      AUDITOR: 'bg-purple-100 text-purple-800',
      PLATFORM_ADMIN: 'bg-amber-100 text-amber-800',
    };
    return role ? colors[role] || 'bg-neutral-100 text-neutral-800' : 'bg-neutral-100 text-neutral-500';
  };

  // Reset page when filters change
  const handleFilterChange = (setter: (v: string) => void, value: string) => {
    setter(value);
    setPage(0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">📋 {t('admin.auditLog.title')}</h1>
        <p className="text-neutral-600 mt-2">
          {t('admin.auditLog.subtitle')}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl p-6 text-white shadow-lg">
          <p className="text-slate-200 text-sm font-medium">{t('admin.auditLog.totalEntries')}</p>
          <p className="text-3xl font-bold mt-2">{total}</p>
        </div>
        <div className="bg-white rounded-xl p-6 border border-neutral-200 shadow-sm">
          <p className="text-neutral-600 text-sm font-medium">{t('admin.auditLog.uniqueActions')}</p>
          <p className="text-3xl font-bold text-neutral-900 mt-2">{uniqueActions.length}</p>
        </div>
        <div className="bg-white rounded-xl p-6 border border-neutral-200 shadow-sm">
          <p className="text-neutral-600 text-sm font-medium">{t('admin.auditLog.displayed')}</p>
          <p className="text-3xl font-bold text-neutral-900 mt-2">{logs.length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4 space-y-4">
        {/* User and Establishment dropdowns */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              👤 {t('admin.auditLog.filterByUser')}
            </label>
            <select
              value={userFilter}
              onChange={(e) => handleFilterChange(setUserFilter, e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            >
              <option value="">{t('admin.auditLog.allUsers')}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.email} {user.displayName ? `(${user.displayName})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              🏢 {t('admin.auditLog.filterByEstablishment')}
            </label>
            <select
              value={establishmentFilter}
              onChange={(e) => handleFilterChange(setEstablishmentFilter, e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            >
              <option value="">{t('admin.auditLog.allEstablishments')}</option>
              {establishments.map((est: any) => (
                <option key={est.id} value={est.id}>
                  {est.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Action filter buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-neutral-700">{t('admin.auditLog.filterByAction')}:</span>
          <button
            onClick={() => handleFilterChange(setActionFilter, '')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              actionFilter === ''
                ? 'bg-slate-600 text-white'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            {t('admin.auditLog.allActions')}
          </button>
          {uniqueActions.slice(0, 5).map((action) => (
            <button
              key={action}
              onClick={() => handleFilterChange(setActionFilter, action)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                actionFilter === action
                  ? 'bg-slate-600 text-white'
                  : `${getActionColor(action)} hover:opacity-80`
              }`}
            >
              {getActionLabel(action)}
            </button>
          ))}
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h2 className="font-semibold text-neutral-900">{t('admin.auditLog.actionHistory')}</h2>
          <span className="text-sm text-neutral-500">
            {t('admin.auditLog.results', { count: logs.length })}
          </span>
        </div>

        {auditLogsQuery.isLoading ? (
          <div className="p-4 sm:p-8 text-center text-neutral-500">{t('common.loading')}</div>
        ) : logs.length === 0 ? (
          <div className="p-4 sm:p-8 text-center text-neutral-500">{t('admin.auditLog.noLogs')}</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase">
                      {t('admin.auditLog.colAction')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase hidden md:table-cell">
                      {t('admin.auditLog.colEntity')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase hidden lg:table-cell">
                      {t('admin.auditLog.colUser')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase hidden lg:table-cell">
                      {t('admin.auditLog.colRole')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase hidden xl:table-cell">
                      {t('admin.auditLog.colEstablishment')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase">
                      {t('admin.auditLog.colDate')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log: any) => (
                    <tr key={log.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                          {getActionLabel(log.action)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-600 hidden md:table-cell">
                        <span className="font-medium">{getEntityLabel(log.entityType)}</span>
                        {log.entityId && (
                          <code className="ml-2 bg-neutral-100 px-2 py-1 rounded text-xs">
                            {log.entityId.substring(0, 8)}...
                          </code>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-600 hidden lg:table-cell">
                        {log.user?.displayName || log.user?.email || (
                          <span className="text-neutral-400 italic">{t('admin.auditLog.system')}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getRoleColor(log.user?.role)}`}>
                          {getRoleDisplay(log.user?.role)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-600 hidden xl:table-cell">
                        {log.establishment?.name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-500 whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleString(i18n.language, {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden space-y-4">
              {logs.map((log: any) => (
                <div key={log.id} className="bg-white rounded-lg border border-neutral-200 p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                      {getActionLabel(log.action)}
                    </span>
                    <span className="text-xs text-neutral-500 font-medium whitespace-nowrap ml-2">
                      {new Date(log.createdAt).toLocaleString(i18n.language, {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-xs font-semibold text-neutral-500 uppercase">{t('admin.auditLog.colEntity')}</p>
                      <p className="font-medium text-neutral-900">{getEntityLabel(log.entityType)}</p>
                      {log.entityId && <code className="text-xs bg-neutral-100 px-2 py-1 rounded inline-block mt-1">{log.entityId.substring(0, 12)}...</code>}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-neutral-500 uppercase">{t('admin.auditLog.colUser')}</p>
                      <p className="text-neutral-700">{log.user?.displayName || log.user?.email || <span className="text-neutral-400 italic">{t('admin.auditLog.system')}</span>}</p>
                    </div>
                    <div className="flex gap-3">
                      <div>
                        <p className="text-xs font-semibold text-neutral-500 uppercase">{t('admin.auditLog.colRole')}</p>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getRoleColor(log.user?.role)}`}>
                          {getRoleDisplay(log.user?.role)}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-neutral-500 uppercase">{t('admin.auditLog.colEstablishment')}</p>
                        <p className="text-neutral-700">{log.establishment?.name || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-600">
            {t('admin.auditLog.showing', { from: page * pageSize + 1, to: Math.min((page + 1) * pageSize, total), total })}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-4 py-2 rounded-lg border border-neutral-200 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.auditLog.previous')}
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * pageSize >= total}
              className="px-4 py-2 rounded-lg border border-neutral-200 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.auditLog.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
